import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { t } from '../lib/i18n';
import { messageFor } from '../lib/errors';
import { formatBytes, formatDate, formatMoney, formatNumber } from '../ui/format';
import { Badge, PageHeader, Panel, type BadgeTone } from '../ui/Page';
import { DataTable, type Column } from '../ui/DataTable';
import {
  cancelScheduledPlanChange,
  changePlan,
  getEntitlements,
  listInvoices,
  planCatalogue,
  type InvoiceView,
  type PlanOffer,
} from './api';

/**
 * The plan: what it costs nothing to show and everything to get wrong.
 *
 * Features and limits are data, never `if (plan === 'PRO')` (context.md section 101) — so this screen
 * renders whatever the entitlements endpoint returns rather than a table written here, and a plan
 * added tomorrow appears without a deploy.
 */

const STATUS_TONE: Record<string, BadgeTone> = {
  ACTIVE: 'positive',
  TRIALING: 'info',
  PAST_DUE: 'warning',
  SUSPENDED: 'danger',
  CANCELED: 'neutral',
};

const INVOICE_TONE: Record<string, BadgeTone> = {
  OPEN: 'warning',
  PAID: 'positive',
  VOID: 'neutral',
  UNCOLLECTIBLE: 'danger',
  DRAFT: 'neutral',
};

export function BillingPage(): React.JSX.Element {
  const entitlements = useQuery({ queryKey: ['entitlements'], queryFn: getEntitlements });
  const invoices = useQuery({ queryKey: ['invoices'], queryFn: listInvoices });

  const plan = entitlements.data;

  const invoiceColumns: Column<InvoiceView>[] = [
    {
      key: 'number',
      header: t('billing.invoiceNumber'),
      render: (invoice) => `#${invoice.number}`,
    },
    {
      key: 'period',
      header: t('billing.period'),
      text: (invoice) => `${invoice.period_start} ${invoice.period_end}`,
      render: (invoice) => (
        <span className="muted small">
          {formatDate(invoice.period_start)} — {formatDate(invoice.period_end)}
        </span>
      ),
    },
    {
      key: 'amount',
      header: t('billing.amount'),
      align: 'end',
      text: (invoice) => String(invoice.amount_cents),
      render: (invoice) => (
        <span className="strong">{formatMoney(invoice.amount_cents, invoice.currency)}</span>
      ),
    },
    {
      key: 'due_at',
      header: t('billing.dueAt'),
      render: (invoice) => <span className="muted small">{formatDate(invoice.due_at)}</span>,
    },
    {
      key: 'status',
      header: t('crm.field.status'),
      text: (invoice) => t(`billing.invoice.${invoice.status}`),
      render: (invoice) => (
        <span className="cell-inline">
          <Badge tone={INVOICE_TONE[invoice.status] ?? 'neutral'}>
            {t(`billing.invoice.${invoice.status}`)}
          </Badge>
          {invoice.status === 'OPEN' && isLate(invoice) && (
            <span className="lead-idle">{t('billing.late')}</span>
          )}
        </span>
      ),
    },
    // Off by default: what somebody asks about only when reconciling a specific payment.
    {
      key: 'paid_at',
      header: t('billing.paidAt'),
      hidden: true,
      render: (invoice) => formatDate(invoice.paid_at),
    },
    {
      key: 'payment_method',
      header: t('billing.paymentMethod'),
      hidden: true,
      render: (invoice) => invoice.payment_method ?? '—',
    },
    {
      key: 'plan_key',
      header: t('billing.plan'),
      hidden: true,
      render: (invoice) => invoice.plan_key,
    },
  ];

  return (
    <div className="view">
      <PageHeader title={t('nav.billing')} description={t('billing.description')} />

      {entitlements.isLoading && <Panel>{t('state.loading')}</Panel>}

      {plan && (
        <>
          <Panel>
            <div className="trial">
              <div>
                <p className="strong">{plan.plan_name}</p>
                <p className="muted small">
                  {plan.status === 'TRIALING' && plan.trial_ends_at
                    ? t('billing.trialUntil').replace('{date}', formatDate(plan.trial_ends_at))
                    : plan.current_period_end
                      ? t('billing.renewsOn').replace('{date}', formatDate(plan.current_period_end))
                      : ''}
                </p>
              </div>

              <Badge tone={STATUS_TONE[plan.status] ?? 'neutral'}>
                {t(`billing.status.${plan.status}`)}
              </Badge>
            </div>
          </Panel>

          {plan.read_only && (
            <p className="alert" role="status">
              {t('error.readOnly')}
            </p>
          )}

          <div className="home-grid">
            <Panel title={t('billing.limits')}>
              <ul className="limit-list">
                {Object.entries(plan.limits).map(([key, value]) => (
                  <li key={key}>
                    <span className="limit-list__label">{t(`limit.${key}`)}</span>
                    <span className="limit-list__value">
                      {key.endsWith('_bytes') ? formatBytes(value) : formatNumber(value)}
                    </span>
                  </li>
                ))}
              </ul>
            </Panel>

            <Panel title={t('billing.features')}>
              <ul className="feature-list">
                {plan.features.map((feature) => (
                  <li key={feature}>
                    <span className="feature-list__tick" aria-hidden="true">
                      ✓
                    </span>
                    {t(`feature.${feature}`)}
                  </li>
                ))}
              </ul>
            </Panel>
          </div>

          <PlanPicker />

          <Panel title={t('billing.invoices')}>
            <DataTable
              tableKey="invoices"
              rows={invoices.data?.invoices}
              columns={invoiceColumns}
              rowKey={(invoice) => invoice.id}
              isLoading={invoices.isLoading}
              error={invoices.error}
              onRetry={() => void invoices.refetch()}
              empty={{ title: t('billing.noInvoices') }}
            />

            {/* The manual provider has no page to send anybody to, and says so rather than
                showing a "pay now" that leads nowhere. */}
            <p className="muted small">{t('billing.howToPay')}</p>
          </Panel>
        </>
      )}
    </div>
  );
}

/** An open invoice whose date has passed. The screen says so before the account changes. */
function isLate(invoice: InvoiceView): boolean {
  return new Date(invoice.due_at).getTime() < Date.now();
}

/**
 * Choosing a plan, with the price of choosing it on the card.
 *
 * The quote comes from the server, already computed, because the two numbers somebody wants before
 * they click — what it costs today and when it takes effect — are exactly the two a browser would
 * get subtly wrong on its own. A card that promises one figure and an invoice that shows another is
 * the fastest way to lose the benefit of letting people upgrade themselves.
 *
 * Blocked plans stay visible and say why. Hiding the plan somebody is too big for would leave them
 * wondering where it went; "o plano Pro permite 6 usuários e você tem 9" is something they can act
 * on.
 */
function PlanPicker(): React.JSX.Element | null {
  const client = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const [confirming, setConfirming] = useState<string | null>(null);

  const catalogue = useQuery({ queryKey: ['plan-catalogue'], queryFn: planCatalogue });

  const refresh = (): void => {
    setConfirming(null);
    setError(null);
    void client.invalidateQueries({ queryKey: ['plan-catalogue'] });
    void client.invalidateQueries({ queryKey: ['entitlements'] });
    void client.invalidateQueries({ queryKey: ['invoices'] });
  };

  const change = useMutation({
    mutationFn: (planKey: string) => changePlan(planKey),
    onSuccess: refresh,
    onError: (caught: unknown) => setError(messageFor(caught)),
  });

  const unschedule = useMutation({
    mutationFn: () => cancelScheduledPlanChange(),
    onSuccess: refresh,
    onError: (caught: unknown) => setError(messageFor(caught)),
  });

  if (catalogue.isLoading) return <Panel>{t('state.loading')}</Panel>;
  if (!catalogue.data) return null;

  const { plans, scheduledPlanKey, scheduledChangeAt } = catalogue.data;
  const busy = change.isPending || unschedule.isPending;

  return (
    <Panel title={t('billing.changePlan')}>
      <p className="muted small">{t('billing.changePlanHint')}</p>

      {scheduledPlanKey && scheduledChangeAt && (
        <p className="alert alert--info cell-inline" role="status">
          <span>
            {t('billing.scheduled')
              .replace(
                '{plan}',
                plans.find((plan) => plan.key === scheduledPlanKey)?.name ?? scheduledPlanKey,
              )
              .replace('{date}', formatDate(scheduledChangeAt))}
          </span>
          <button
            type="button"
            className="button button--quiet button--compact"
            disabled={busy}
            onClick={() => unschedule.mutate()}
          >
            {t('billing.cancelScheduled')}
          </button>
        </p>
      )}

      {error && (
        <p className="alert" role="alert">
          {error}
        </p>
      )}

      <div className="plan-grid">
        {plans.map((plan) => (
          <PlanCard
            key={plan.key}
            plan={plan}
            busy={busy}
            confirming={confirming === plan.key}
            onAsk={() => {
              setError(null);
              setConfirming(plan.key);
            }}
            onCancel={() => setConfirming(null)}
            onConfirm={() => change.mutate(plan.key)}
          />
        ))}
      </div>
    </Panel>
  );
}

function PlanCard({
  plan,
  busy,
  confirming,
  onAsk,
  onCancel,
  onConfirm,
}: {
  plan: PlanOffer;
  busy: boolean;
  confirming: boolean;
  onAsk: () => void;
  onCancel: () => void;
  onConfirm: () => void;
}): React.JSX.Element {
  const blocked = plan.blockers.length > 0;

  return (
    <article className={`plan-card${plan.current ? ' plan-card--current' : ''}`}>
      <header className="plan-card__head">
        <span className="strong">{plan.name}</span>
        {plan.current && <Badge tone="positive">{t('billing.currentPlan')}</Badge>}
        {plan.scheduled && <Badge tone="info">{t('billing.scheduledPlan')}</Badge>}
      </header>

      <p className="plan-card__price">
        {formatMoney(plan.priceCents, plan.currency)}
        <span className="muted small"> {t(`billing.per.${plan.billingPeriod}`)}</span>
      </p>

      <p className="muted small">{plan.description}</p>

      {plan.quote && !blocked && (
        <p className="small">
          {plan.quote.effective === 'NOW'
            ? plan.quote.chargeCents > 0
              ? t('billing.quoteNow').replace(
                  '{value}',
                  formatMoney(plan.quote.chargeCents, plan.currency),
                )
              : t('billing.quoteFree')
            : t('billing.quoteAtPeriodEnd')}
        </p>
      )}

      {blocked && (
        <ul className="plan-card__blockers">
          {plan.blockers.map((blocker) => (
            <li key={blocker.key} className="small">
              {t('billing.blocker')
                .replace('{limit}', t(`limit.${blocker.key}`))
                .replace(
                  '{allowed}',
                  blocker.key.endsWith('_bytes')
                    ? formatBytes(blocker.limit)
                    : formatNumber(blocker.limit),
                )
                .replace(
                  '{current}',
                  blocker.key.endsWith('_bytes')
                    ? formatBytes(blocker.current)
                    : formatNumber(blocker.current),
                )}
            </li>
          ))}
        </ul>
      )}

      {!plan.current &&
        (confirming ? (
          <div className="cell-inline">
            <button
              type="button"
              className="button button--primary button--compact"
              disabled={busy}
              onClick={onConfirm}
            >
              {t('billing.confirmChange')}
            </button>
            <button
              type="button"
              className="button button--quiet button--compact"
              disabled={busy}
              onClick={onCancel}
            >
              {t('confirm.cancel')}
            </button>
          </div>
        ) : (
          <button
            type="button"
            className="button button--compact"
            disabled={busy || blocked}
            onClick={onAsk}
          >
            {plan.quote?.kind === 'DOWNGRADE' ? t('billing.downgradeTo') : t('billing.upgradeTo')}
          </button>
        ))}
    </article>
  );
}
